import { useMemo } from "react";
import { useIntl } from "react-intl";
import { GQL, Utils } from "@binale-tech/shared";

export const BEWIRTUNG_FIELD = "bewirtung";

/** A Bewirtung while it is edited: a cleared amount or date input leaves a gap that validation reports. */
export type BewirtungDraft = Omit<GQL.IDmsBewirtungInput, "amountCents" | "date"> & {
    amountCents: number | null;
    date: string | undefined;
};

/** The slice of any parent form that holds the Bewirtung; null means removed. */
export type BewirtungFormValues = {
    bewirtung?: BewirtungDraft | null;
};

export const splitParticipants = (text: string): string[] => text.split(/\r?\n/);

const filledParticipants = (participants: readonly string[] | undefined): string[] =>
    (participants ?? []).map((name) => name.trim()).filter(Boolean);

const isNonNegativeInteger = (value: number): boolean => Number.isInteger(value) && value >= 0;

export const normalizeBewirtungInput = (value: BewirtungDraft): GQL.IDmsBewirtungInput => ({
    templateVersion: 1,
    kind: value.kind,
    date: value.date ?? "",
    place: value.place.trim(),
    participants: filledParticipants(value.participants),
    occasion: value.occasion?.trim() || null,
    amountCents: value.amountCents ?? 0,
    tipCents: value.tipCents ?? null,
    host: value.host.trim(),
});

export const useBewirtungRules = () => {
    const intl = useIntl();
    return useMemo(() => {
        const required = intl.formatMessage({ id: "app.validation.required" });
        const pattern = intl.formatMessage({ id: "app.validation.pattern" });
        const occasionRequired = intl.formatMessage({ id: "app.dms.bewirtung.occasionRequired" });
        const notBlank = (v: string | undefined) => (v ?? "").trim().length > 0 || required;
        return {
            text: { validate: notBlank },
            participants: {
                validate: (v: string[] | undefined) => filledParticipants(v).length > 0 || required,
            },
            date: {
                validate: (v: string | undefined) =>
                    !v ? required : Utils.BewirtungPrefill.isCalendarDate(v) || pattern,
            },
            occasion: {
                validate: (v: string | null | undefined, values: BewirtungFormValues) =>
                    values.bewirtung?.kind !== GQL.IDmsBewirtungKind.Business ||
                    (v ?? "").trim().length > 0 ||
                    occasionRequired,
            },
            // AmountInput shows 0 as an empty field, so 0 counts as not entered.
            amount: {
                validate: (v: number | null | undefined) => (!v ? required : isNonNegativeInteger(v) || pattern),
            },
            tip: { validate: (v: number | null | undefined) => v == null || isNonNegativeInteger(v) || pattern },
        };
    }, [intl]);
};
